import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, MapPin, Package, TimerOff } from 'lucide-react';
import PriceCountdown from './PriceCountdown';
import { getCustomerBadges, getRoleBadgeInfo } from '../../utils/formatters';
import { useLanguage } from '../../context/LanguageContext';

function checkExpired(listing) {
  if (!listing.expiresAt) return false;
  return new Date(listing.expiresAt).getTime() <= Date.now();
}

export default function ListingCard({ listing, featured = false }) {
  const { t, tCond } = useLanguage();
  if (!listing) return null;

  // Support both API shape and legacy mock shape
  const id         = listing.id;
  const listingNo  = listing.listingNo;
  const title      = listing.title;
  const images     = listing.images || [];
  const condition  = listing.condition;
  const viewCount  = listing.viewCount ?? listing.views ?? 0;
  const city       = listing.city || listing.location?.city || '';
  const isReserved = listing.isReserved ?? listing.reserved ?? false;
  const isFeatured = featured || listing.isFeatured;
  const isListingExpired = checkExpired(listing);

  // Seller — API returns seller.name, seller.isVerified, seller.role (numeric)
  const seller = listing.seller || {};
  const sellerName     = seller.name || seller.userNameSurname || '?';
  const sellerRole     = seller.role ?? 1;
  const sellerVerified = seller.isVerified ?? seller.verified ?? false;
  const sellerBadges   = seller.badges || [];
  const sellerPhoto    = seller.userPhoto;

  const customerBadges = getCustomerBadges(sellerBadges);
  const roleBadge      = getRoleBadgeInfo(sellerRole);
  // Show role badge only for non-normal roles
  const showRoleBadge  = sellerRole !== 1 && sellerRole !== 'normal';

  return (
    <Link to={`/listing/${id}`} className="group block">
      <div className={`card hover:shadow-apple-md transition-all duration-300 group-hover:-translate-y-0.5 ${isFeatured ? 'ring-2 ring-apple-blue/20' : ''} ${isListingExpired ? 'opacity-70' : ''}`}>
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-apple-gray rounded-t-2xl">
          {images[0] ? (
            <img
              src={images[0]}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl">📦</div>
          )}

          {isFeatured && (
            <span className="absolute top-2 left-2 bg-apple-blue text-white text-xs font-semibold px-2 py-0.5 rounded-full">
              {t('home.featured_eyebrow')}
            </span>
          )}
          {isReserved && !isListingExpired && (
            <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
              {t('price_countdown.reserved')}
            </span>
          )}
          {isListingExpired && (
            <span className="absolute top-2 right-2 flex items-center gap-1 bg-gray-700 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
              <TimerOff className="w-3 h-3" /> {t('price_countdown.expired_short')}
            </span>
          )}
          <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
            <Eye className="w-3 h-3" />
            {viewCount.toLocaleString('de-DE')}
          </div>
        </div>

        {/* Content */}
        <div className="p-3">
          <h3 className="font-medium text-apple-gray-dark text-sm leading-snug line-clamp-2 mb-1 group-hover:text-apple-blue transition-colors">
            {title}
          </h3>

          <PriceCountdown listing={listing} compact />

          <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-1 text-xs text-apple-gray-mid">
              <MapPin className="w-3 h-3" />
              <span>{city || '—'}</span>
            </div>
            {condition && (
              <div className="flex items-center gap-1 text-xs text-apple-gray-mid">
                <Package className="w-3 h-3" />
                <span>{tCond(condition)}</span>
              </div>
            )}
          </div>

          {/* Seller */}
          <div className="flex items-center gap-2 mt-2">
            {sellerPhoto ? (
              <img src={sellerPhoto} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-5 h-5 rounded-full bg-apple-blue flex items-center justify-center shrink-0">
                <span className="text-white text-[9px] font-bold">{sellerName[0]?.toUpperCase()}</span>
              </div>
            )}
            <span className="text-xs text-apple-gray-mid truncate flex-1">{sellerName}</span>
            {showRoleBadge && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${roleBadge.className}`}>
                {t(roleBadge.labelKey)}
              </span>
            )}
            {sellerVerified && (
              <span className="text-apple-blue text-xs" title={t('badges.verified')}>✓</span>
            )}
          </div>

          {/* Customer badges */}
          {customerBadges.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {customerBadges.map(b => (
                <span key={b.labelKey} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${b.className}`}>
                  {b.icon} {t(b.labelKey)}
                </span>
              ))}
            </div>
          )}

          <p className="text-[10px] text-apple-gray-mid mt-1.5">#{listingNo}</p>
        </div>
      </div>
    </Link>
  );
}
